import React from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { AdBlock } from "../ads/AdBlock";
import {
  Info,
  FileText,
  Shield,
  Cookie,
  HelpCircle,
  Mail,
  ExternalLink,
  Facebook,
  Twitter,
  Instagram,
  Pin,
} from "lucide-react";

const FooterContainer = styled.footer`
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(10px);
  padding: var(--spacing-6) 0;
  margin-top: auto;
  
  @media (max-width: 768px) {
    padding: var(--spacing-4) 0;
  }
  
  .desktop-only-ad {
    @media (max-width: 768px) {
      display: none !important;
    }
  }
`;

const FooterContent = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 var(--spacing-4);
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-6);
  margin-bottom: var(--spacing-6);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-4);
    margin-bottom: var(--spacing-4);
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);

  &.legal-section {
    @media (max-width: 768px) {
      margin-top: 8px;
      margin-bottom: 8px;
      padding-top: 0;
      padding-bottom: 0;
    }
  }
`;

const SectionTitle = styled.h3`
  color: var(--color-text-primary);
  font-size: 1.1rem;
  margin-bottom: var(--spacing-2);
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
`;

const FooterLink = styled(Link)`
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  gap: var(--spacing-2);

  &:hover {
    color: var(--color-primary);
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: var(--spacing-3);
`;

const SocialLink = styled.a`
  color: var(--color-text-secondary);
  transition: all 0.2s ease;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);

  &:hover {
    color: var(--color-primary);
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.1);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const PinterestIcon = styled(Pin)`
  color: inherit;
  transform: scale(1.2);
`;

const Copyright = styled.div`
  text-align: center;
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  padding-top: var(--spacing-4);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

export const Footer = () => {
  return (
    <FooterContainer>
      <FooterContent>
        <FooterGrid>
          <FooterSection className="legal-section">
            <SectionTitle data-translate="true">
              <Info size={20} />
              Informations Légales
            </SectionTitle>
            <FooterLink to="/legal-notice" data-translate="true">
              <FileText size={16} />
              Mentions Légales
            </FooterLink>
            <FooterLink to="/terms" data-translate="true">
              <Shield size={16} />
              CGU
            </FooterLink>
            <FooterLink to="/privacy" data-translate="true">
              <Shield size={16} />
              Politique de Confidentialité
            </FooterLink>
            <FooterLink to="/cookies" data-translate="true">
              <Cookie size={16} />
              Cookies
            </FooterLink>
          </FooterSection>

          <FooterSection className="legal-section">
            <SectionTitle data-translate="true">
              <HelpCircle size={20} />
              Aide
            </SectionTitle>
            <FooterLink to="/faq" data-translate="true">
              <HelpCircle size={16} />
              FAQ
            </FooterLink>
            <FooterLink to="/contact" data-translate="true">
              <Mail size={16} />
              Contact
            </FooterLink>
          </FooterSection>

          <FooterSection className="legal-section">
            <SectionTitle data-translate="true">
              <ExternalLink size={20} />
              Réseaux Sociaux
            </SectionTitle>
            <SocialLinks>
              <SocialLink
                href="https://www.facebook.com/profile.php?viewas=100000686899395&id=61569917156127"
                target="_blank"
                rel="noopener noreferrer"
                data-translate="true"
              >
                <Facebook />
              </SocialLink>
              <SocialLink
                href="https://twitter.com/MartinR35832934"
                target="_blank"
                rel="noopener noreferrer"
                data-translate="true"
              >
                <Twitter />
              </SocialLink>
              <SocialLink
                href="https://www.instagram.com/cinedelux/"
                target="_blank"
                rel="noopener noreferrer"
                data-translate="true"
              >
                <Instagram />
              </SocialLink>
              <SocialLink
                href="https://pinterest.com/cinedelux"
                target="_blank"
                rel="noopener noreferrer"
                data-translate="true"
              >
                <PinterestIcon />
              </SocialLink>
            </SocialLinks>
          </FooterSection>
        </FooterGrid>
        
        {/* Bannière publicitaire juste au-dessus du copyright - MASQUÉE sur mobile */}
        <div style={{ margin: '20px 0' }} className="desktop-only-ad">
          <AdBlock adSlot="footer-banner" format="banner" desktopOnly />
        </div>

        <Copyright data-translate="true">
          &copy; {new Date().getFullYear()} Cinedelux. Tous droits réservés.
        </Copyright>
      </FooterContent>
    </FooterContainer>
  );
};
